import { useContext, useEffect, useMemo, useState } from 'react';
import { startCase } from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { getConfig } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PP_STRATEGIC_OPPORTUNITY_COLOR_BY_CHANGED,
    PP_STRATEGIC_OPPORTUNITY_SCATTER_DIMENSION_CHANGED,
} from 'utils/mixpanelEventNames';

type SubFilter = {
    dataKey: string;
    dimension: string;
    isAttribute: boolean;
    name?: string;
};

const mapAttrs = (attrs: any[], dimension: string) =>
    (attrs ?? []).map((item: any) => ({
        ...item,
        key: item.name,
        label: startCase(item.name),
        dimension: item.dimensionReference ?? dimension,
    }));

const useStrategicOpportunityAttributeLevels = (
    defaultSubFilter: SubFilter,
    defaultColoredBy: any,
) => {
    const { appConfig } = useContext(UserContext);
    const { allDimAndAttributeTypeList } = useContext(FilterContextData);

    const [selectedSubFilter, setSelectedSubFilter] = useState<SubFilter>(defaultSubFilter);
    const [selectedColoredBy, setSelectedColoredBy] = useState<any>(defaultColoredBy);

    const dim1AttributeList = useMemo(() => {
        const dims = allDimAndAttributeTypeList?.dimentions ?? [];
        const dim1Attrs = dims.find((d: any) => d?.dimensionReference === 'dim1')?.attribute ?? [];
        const dim2Attrs = dims.find((d: any) => d?.dimensionReference === 'dim2')?.attribute ?? [];

        // Key missing → dim1 only. Key present → dim1 + dim2 from DB, minus excluded names.
        const excluded = getConfig('profitPool', appConfig)?.strategicOpportunityExcludedFilterKeys;
        if (excluded == null) {
            return mapAttrs(dim1Attrs, 'dim1');
        }

        const bannedDim1 = new Set(excluded.dim1 ?? []);
        const bannedDim2 = new Set(excluded.dim2 ?? []);
        return [
            ...mapAttrs(dim1Attrs, 'dim1').filter((item: any) => !bannedDim1.has(item.name)),
            ...mapAttrs(dim2Attrs, 'dim2').filter((item: any) => !bannedDim2.has(item.name)),
        ];
    }, [allDimAndAttributeTypeList, appConfig]);

    const coloredByOptions = dim1AttributeList;

    // If default / current Scattered by is missing from the filtered list, snap to first available level.
    useEffect(() => {
        if (!dim1AttributeList?.length) {
            return;
        }
        const stillPresent = dim1AttributeList.some((item: any) => item.name === selectedSubFilter.dataKey);
        if (stillPresent) {
            return;
        }
        const next = dim1AttributeList[0];
        setSelectedSubFilter({
            ...next,
            dataKey: next.name,
        });
        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_SCATTER_DIMENSION_CHANGED, {
            dimension_name: next.name,
        });
    }, [dim1AttributeList, selectedSubFilter.dataKey]);

    // If default / current Colors by is missing from the list, snap to first available level.
    useEffect(() => {
        if (!dim1AttributeList?.length) {
            return;
        }
        const stillPresent = dim1AttributeList.some((item: any) => item.key === selectedColoredBy?.key);
        if (stillPresent) {
            return;
        }
        const nextColoredBy = dim1AttributeList[0];
        setSelectedColoredBy(nextColoredBy);
        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_COLOR_BY_CHANGED, {
            color_by: nextColoredBy?.label ?? 'Not Available',
            dimension_name: nextColoredBy?.dimension ?? '',
        });
    }, [dim1AttributeList, selectedColoredBy?.key]);

    return {
        dim1AttributeList,
        coloredByOptions,
        selectedSubFilter,
        setSelectedSubFilter,
        selectedColoredBy,
        setSelectedColoredBy,
    };
};

export default useStrategicOpportunityAttributeLevels;
